/**
 * Per-attachment content-key crypto for Binder image attachments. A fresh
 * AES-256-GCM key encrypts the raw image (and thumbnail) bytes; the raw key is
 * then ECIES-wrapped to the family public key via `@heybesafe/shared`
 * wrapAttachmentKey. Distinct from crypto.ts:encryptItem, which is JSON-only.
 */

export async function generateContentKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

export async function exportContentKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', key));
}

export async function importContentKey(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

export async function encryptBytes(
  bytes: Uint8Array,
  key: CryptoKey,
): Promise<{ ciphertext: Uint8Array; iv: Uint8Array }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes as BufferSource));
  return { ciphertext, iv };
}

export async function decryptBytes(ciphertext: Uint8Array, iv: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, ciphertext.buffer as ArrayBuffer),
  );
}
